import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useState, useEffect, use } from 'react';
import { trpc } from '@/app/_trpc/client';
import {
  DialogKey,
  DialogContext,
} from '@/components/features/dialogs/infra/context/DialogContext';

const initialOnboardingSteps = [
  { id: 'verify-id', link: '/verify-user' },
  { id: 'verify-email', dialogId: 'email-verification' },
  { id: 'add-picture', dialogId: 'photo-upload' },
  { id: 'add-languages', link: '/edit-profile?focusOn=languages' },
  { id: 'tell-story', link: '/edit-profile?focusOn=story' },
  { id: 'add-industry', link: '/edit-profile?focusOn=career' },
  { id: 'add-availability', link: '/edit-profile?focusOn=availability' },
] as const;

export interface OnboardingStep {
  id: (typeof initialOnboardingSteps)[number]['id'];
  link?: string;
  completed?: boolean;
  dialogId?: DialogKey;
}

export function useOnboardingSteps() {
  const router = useRouter();
  const [onboardingSteps, setOnboardingSteps] = useState<OnboardingStep[]>([
    ...initialOnboardingSteps,
  ]);
  const { data: session, status } = useSession();
  const userId = session?.user?.id;

  const { data: user, isLoading: isLoadingUser } = trpc.users.getById.useQuery(
    { id: userId as string },
    { enabled: !!userId && status === 'authenticated' }
  );

  const isLoading = isLoadingUser || status === 'loading';
  const { openDialog } = use(DialogContext);

  useEffect(() => {
    if (user?.idVerificationStatus) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'verify-id' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.emailVerified) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'verify-email' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.profile?.profilePhoto) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'add-picture' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.languages && user?.languages.length > 0) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'add-languages' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.profile?.myStory) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'tell-story' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.profile?.currentIndustry) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'add-industry' ? { ...step, completed: true } : step
        )
      );
    }
    if (user?.availability && user?.availability.length > 0) {
      setOnboardingSteps((prev) =>
        prev.map((step) =>
          step.id === 'add-availability' ? { ...step, completed: true } : step
        )
      );
    }
  }, [user]);

  const completionPercentage = Math.round(
    Math.round(
      (onboardingSteps.filter((step) => step.completed).length /
        onboardingSteps.length) *
        100
    )
  );

  const handleStepClick = (step: OnboardingStep) => {
    if (step.dialogId) {
      openDialog(step.dialogId, {});
    } else if (step.link) {
      router.push(step.link);
    }
  };

  return {
    onboardingSteps,
    setOnboardingSteps,
    completionPercentage,
    handleStepClick,
    isLoading,
  };
}
